import React, { useState, useEffect } from 'react';
import './LoadingIndicator.css';

// Enhanced loading indicator with cold start detection
const LoadingIndicator = ({
    isVisible,
    message = "Searching...",
    showColdStartWarning = false,
    duration = 0,
    isReindexing = false,
    operationType = "search"
}) => {
    const [showColdStartNotice, setShowColdStartNotice] = useState(false);
    const [elapsedTime, setElapsedTime] = useState(0);

    useEffect(() => {
        if (!isVisible) {
            setShowColdStartNotice(false);
            setElapsedTime(0);
            return;
        }

        // Show cold start notice after 5 seconds of loading
        const coldStartTimer = setTimeout(() => {
            setShowColdStartNotice(true);
        }, 5000);

        // Track elapsed time with reduced frequency to minimize forced reflows
        const startTime = Date.now();
        const timeTracker = setInterval(() => {
            // Use requestAnimationFrame to batch DOM updates
            requestAnimationFrame(() => {
                setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
            });
        }, 2000); // Reduced from 1000ms to 2000ms to minimize forced reflows

        return () => {
            clearTimeout(coldStartTimer);
            clearInterval(timeTracker);
        };
    }, [isVisible]);

    if (!isVisible) return null;

    return (
        <div className="loading-indicator-overlay">
            <div className="loading-indicator-container">
                <div className="spinner-container">
                    <div className="spinner"></div>
                </div>

                <div className="loading-content">
                    <h3>{message}</h3>

                    {elapsedTime > 0 && (
                        <div className="elapsed-time">
                            {elapsedTime}s
                        </div>
                    )}

                    {showColdStartNotice && (
                        <div className="cold-start-notice">
                            <div className="cold-start-icon">{isReindexing ? '🔄' : '❄️'}</div>
                            <p><strong>
                                {isReindexing
                                    ? `Processing ${operationType === 'cv' ? 'CV files' : 'proposal files'}...`
                                    : 'Initializing search services...'
                                }
                            </strong></p>
                            <small>
                                {isReindexing
                                    ? `${operationType === 'cv' ? 'CV reindexing' : 'Proposal vectorization'} may take several minutes depending on the number of files.`
                                    : 'First search after inactivity may take 30-60 seconds.\nSubsequent searches will be much faster.'
                                }
                            </small>
                        </div>
                    )}

                    {!showColdStartNotice && elapsedTime > 3 && (
                        <small className="loading-hint">
                            {isReindexing
                                ? `Processing ${operationType === 'cv' ? 'CV' : 'proposal'} files...`
                                : 'Searching through documents...'
                            }
                        </small>
                    )}
                </div>
            </div>
        </div>
    );
};

// Simple cold start indicator for specific use cases
export const ColdStartIndicator = ({ isVisible }) => {
    if (!isVisible) return null;

    return (
        <div className="cold-start-notice">
            <div className="spinner"></div>
            <p>🔄 Initializing search services...</p>
            <small>First search may take 30-60 seconds</small>
        </div>
    );
};

export default LoadingIndicator; 